import type { ResolvedToolDefinition, ToolContentListItem } from "@/data/tools";
import Link from "next/link";
import ToolIcon from "./ToolIcon";
import ToolSectionHeader from "./ToolSectionHeader";

type ToolAudiencesSectionProps = {
  tool: ResolvedToolDefinition;
};

function ContentList({
  items,
  accent,
}: {
  items: readonly ToolContentListItem[];
  accent: "primary" | "accent";
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {items.map((item) => {
        const itemAccent = item.accent ?? accent;
        const isAccent = itemAccent === "accent";
        const content = (
          <div className="flex items-start gap-3">
            {item.icon ? (
              <div
                className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border bg-white/90 shadow-sm ${
                  isAccent ? "border-[#f7d6e4]" : "border-[#ece4ff]"
                }`}
              >
                <ToolIcon
                  icon={item.icon}
                  size={17}
                  className={isAccent ? "text-[#c3003a]" : "text-[#5b32b4]"}
                />
              </div>
            ) : null}

            <div className="min-w-0 space-y-1">
              <h3 className="text-sm font-semibold leading-5 text-[#2b1d3a]">
                {item.label}
              </h3>

              {item.description ? (
                <p className="text-[13px] leading-5 text-gray-600">
                  {item.description}
                </p>
              ) : null}
            </div>
          </div>
        );

        const className = `group rounded-xl border px-4 py-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm ${
          isAccent
            ? "border-[#f4d7e6] bg-gradient-to-br from-white to-[#fff7fb] hover:border-[#fb397d]/30 hover:shadow-[#fb397d]/10"
            : "border-[#e9e1ff] bg-gradient-to-br from-white to-[#faf7ff] hover:border-[#5b32b4]/30 hover:shadow-[#5b32b4]/10"
        }`;

        return item.href ? (
          <Link key={item.label} href={item.href} className={className}>
            {content}
          </Link>
        ) : (
          <article
            key={item.label}
            className={className}
          >
            {content}
          </article>
        );
      })}
    </div>
  );
}

export default function ToolAudiencesSection({
  tool,
}: ToolAudiencesSectionProps) {
  const audiences = tool.pageContent?.audiences ?? [];

  if (!audiences.length) {
    return null;
  }

  return (
    <section
      className="mx-auto max-w-4xl space-y-5"
      aria-labelledby="audiences-heading"
    >
      <ToolSectionHeader
        headingId="audiences-heading"
        title="Who This Is For"
        description="The people and teams this tool is especially useful for."
      />

      <ContentList items={audiences} accent="accent" />
    </section>
  );
}
